import React, { useEffect, useState } from "react";
import { clientPreview } from "../../../../api/services/services";
import { Button, Snackbar, Typography, Box, CircularProgress, Grid, Stack } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Bucket } from "../../../../api/config/apiConfig";
import { useLocation } from "react-router-dom";
import moment from "moment";
import gifLoading from "../../../../imgs/giphy.gif";
import LazyImage from "../../LazyLoadImage";
import PremiumPublicLayout from "../../../templates/PremiumPublicLayout";
import PremiumCard from "../../../molecules/PremiumCard";
import SectionHeader from "../../../molecules/SectionHeader";
import PrintComponent from "../../PrintComponent";
import SurveyPreviewDownload from "../download";

import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const ClientSurveyPreview = () => {
  const [surveyData, setSurveyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const location = useLocation();
  const urlData = new URLSearchParams(location.search);
  const email = urlData.get("email");
  const surveyId = urlData.get("surveyId");
  const token = urlData.get("token");

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const getClientPreview = () => {
    clientPreview(email, surveyId, token)
      .then((response) => {
        setSurveyData(response?.data?.data);
        setIsValid(true);
      })
      .catch((error) => {
        setSnackbarMessage(error?.response?.data?.message || "Failed to load survey");
        setOpenSnackbar(true);
        setIsValid(false);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getClientPreview();
  }, []);

  const formatDate = (unix) => {
    if (!unix) return "-";
    return moment(unix * 1000).format("Do MMMM, YYYY");
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <CircularProgress />
    </Box>
  );

  if (!isValid) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h5" color="error">Invalid link or survey data not available.</Typography>
    </Box>
  );

  const detail = surveyData?.surveyDetail;
  let totals = { weight: 0, cbm: 0, items: 0 };
  surveyData?.roomWiseCalculation?.forEach(cat => {
    totals.weight += Number(cat.totalWeightKg || 0);
    totals.cbm += Number(cat.totalCbm || 0);
    totals.items += Number(cat.totalItems || 0);
  });

  const printAction = () => {
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const pageActions = (
    <Button
      variant="contained"
      startIcon={<DescriptionIcon />}
      onClick={() => {
        if (window.innerWidth <= 768) { if (typeof setSnackbarMessage !== "undefined" && typeof setOpenSnackbar !== "undefined") { setSnackbarMessage("For a good experience, please use a desktop screen"); setOpenSnackbar(true); } else { alert("For a good experience, please use a desktop screen"); } return; }
                                const tempTitle = document.title;
        document.title = `Survey Preview-${detail?.name}-${moment().format("DD.MM.YYYY")}`;
        printAction();
        document.title = tempTitle;
      }}
      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, background: "linear-gradient(45deg, #1976d2, #1565c0)" }}
    >
      Save as PDF
    </Button>
  );

  return (
    <PremiumPublicLayout title="Survey Preview" actions={pageActions} hideBackButton={true}>
      <div className="no-print">
        <SectionHeader title="Customer Information" icon={<PersonIcon />} />
        <PremiumCard sx={{ mb: 4, p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, color: '#1a202c' }}>{detail?.name}</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#eff6ff', borderRadius: '10px' }}><EmailIcon color="primary" /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{detail?.email}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#eff6ff', borderRadius: '10px' }}><PhoneIcon color="primary" /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Phone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{detail?.loadPlacePhone || "-"}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#fff7ed', borderRadius: '10px' }}><LocationOnIcon sx={{ color: '#ea580c' }} /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Origin</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{detail?.loadPlaceStAddress}, {detail?.loadPlaceCity}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: '10px' }}><LocationOnIcon sx={{ color: '#16a34a' }} /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Destination</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{detail?.unloadPlaceCity}, {detail?.unloadPlaceCountry}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#f5f3ff', borderRadius: '10px' }}><CalendarMonthIcon sx={{ color: '#7c3aed' }} /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Removal Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(detail?.loadDate)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#fdf2f8', borderRadius: '10px' }}><CalendarMonthIcon sx={{ color: '#db2777' }} /></Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Survey Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(detail?.surveyDate)}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          {detail?.description && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b' }}>"{detail.description}"</Typography>
            </Box>
          )}
        </PremiumCard>

        <SectionHeader title="Survey Overview" icon={<ListAltIcon />} />
        <PremiumCard sx={{ mb: 4, overflow: 'hidden' }}>
          <Box sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', bgcolor: '#f8fafc', p: 2, fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ flex: 2 }}>Room</Box>
              <Box sx={{ flex: 1 }}>Weight (KG)</Box>
              <Box sx={{ flex: 1 }}>CBM</Box>
              <Box sx={{ flex: 1 }}>Items</Box>
            </Box>
            {surveyData?.roomWiseCalculation?.map((cat, idx) => (
              <Box key={idx} sx={{ display: 'flex', p: 2, borderBottom: idx === surveyData.roomWiseCalculation.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <Box sx={{ flex: 2, fontWeight: 600 }}>{cat.roomName}</Box>
                <Box sx={{ flex: 1 }}>{Number(cat.totalWeightKg || 0).toFixed(2)}</Box>
                <Box sx={{ flex: 1 }}>{Number(cat.totalCbm || 0).toFixed(2)}</Box>
                <Box sx={{ flex: 1 }}>{cat.totalItems}</Box>
              </Box>
            ))}
            <Box sx={{ display: 'flex', bgcolor: '#eff6ff', p: 2, fontWeight: 800, color: '#1e40af' }}>
              <Box sx={{ flex: 2 }}>TOTAL</Box>
              <Box sx={{ flex: 1 }}>{totals.weight.toFixed(2)}</Box>
              <Box sx={{ flex: 1 }}>{totals.cbm.toFixed(2)}</Box>
              <Box sx={{ flex: 1 }}>{totals.items}</Box>
            </Box>
          </Box>
        </PremiumCard>

        {detail?.surveyResult?.surveyRooms?.length > 0 && (
          <>
            <SectionHeader title="Room Details & Photos" icon={<MeetingRoomIcon />} />
            {detail.surveyResult.surveyRooms.map((sr, idx) => (
              <PremiumCard key={idx} sx={{ mb: 4, p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: '#1e293b', borderLeft: '4px solid #3b82f6', pl: 2 }}>{sr.name}</Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {sr.images?.map((img, i) => (
                        <Box key={i} sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                          <LazyImage src={Bucket + img} placeholderSrc={gifLoading} height={200} width={150} alt={`Room ${idx}`} />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      {['movable', 'nonmovable'].map(type => (
                        <Box key={type} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                          <Box sx={{ bgcolor: type === 'movable' ? '#3b82f6' : '#64748b', color: 'white', p: 1, px: 2, fontWeight: 'bold' }}>
                            {type === 'movable' ? 'Moving' : 'Not Moving'}
                          </Box>
                          <Box sx={{ p: 2 }}>
                            {sr.roomItems?.filter(item => item.type === type).length > 0 ? (
                              sr.roomItems.filter(item => item.type === type).map((item, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px dashed #e2e8f0' }}>
                                  <Typography variant="body2">{item.name}</Typography>
                                  <Typography variant="body2" fontWeight="bold">{item.quantity}x</Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>No items listed</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                      {sr.remarks && (
                        <Box sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>Remarks:</Typography>
                          <Typography variant="body2">{sr.remarks}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </PremiumCard>
            ))}
          </>
        )}
      </div>

      <PrintComponent>
        <SurveyPreviewDownload id="content-id" surveyData={surveyData} />
      </PrintComponent>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <MuiAlert elevation={6} variant="filled" severity="error" onClose={handleCloseSnackbar}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </PremiumPublicLayout>
  );
};

export default ClientSurveyPreview;
